<!-- Task Sidebar Container -->
<div class="task-sidebar-wrapper">
  <!-- Main Sidebar (with animation) -->
  <div class="task-sidebar" [@sidebarCollapse]="getSidebarAnimationState()"
    [class.task-sidebar--collapsed]="isCollapsed">

    <!-- Sidebar Header -->
    <div class="task-sidebar__header">
      <!-- Project Icon & Task Info -->
      <div class="task-sidebar__task-info">
        <div class="task-info__icon">
          <mat-icon class="hash-icon">task_alt</mat-icon>
        </div>
        <div class="task-info__details">
          <div class="task-info__project">{{ projectName || 'Project' }}</div>
          <div class="task-info__task-id">Task #{{ taskId || 'New' }}</div>
        </div>
      </div>

      <!-- Collapse Button -->
      <button type="button" class="sidebar-collapse-btn" (click)="collapseSidebar()"
        [attr.aria-label]="'Collapse sidebar'">
        <mat-icon>arrow_back</mat-icon>
      </button>
    </div>

    <!-- Search Input -->
    @if (!isCollapsed && isViewMode) {
      <div class="task-sidebar__search">
        <mat-icon class="search-icon">search</mat-icon>
        <input type="text" class="search-input" [value]="searchQuery" (input)="handleSearch($any($event.target).value)"
          [disabled]="isViewMode || isReadOnly" placeholder="Search task details..."
          [attr.aria-label]="'Search task details'">
      </div>
    }

    <!-- Sidebar Content (scrollable sections) -->
    @if (!isCollapsed) {
      <div class="task-sidebar__content" [ngClass]="{'task-sidebar__content--no-search': !isViewMode}"
        >
        <!-- PRE-STATUS SECTION: SELF ASSIGN / MODULE / REFERENCE (STATIC ONLY) -->
        <!-- Hide entire section when: 1) editing existing task, OR 2) in view mode, OR 3) both module_id and reference_id are pre-filled -->
        @if (showModuleControls && !isViewMode && !isEditingExistingTask()) {
          <div class="sidebar-section">
            <div class="section-content">
              <!-- Row 1: Self assign toggle (static) -->
              <div class="form-field form-field--inline">
                <label class="field-label">Self assign</label>
                <mat-slide-toggle class="b-one-switch toggle-input" [checked]="$any(taskForm?.get('self_assign')?.value) == 1"
                (change)="onSelfAssignToggle($event)"></mat-slide-toggle>
              </div>
              <!-- Row 2: Module dropdown (dynamic, styled like Priority select) -->
              @if (!($any(taskForm?.get('self_assign')?.value) == 1)) {
                <div class="form-field">
                  <label class="field-label"> Select Module</label>
                  <div class="custom-select custom-select--module">
                    <select class="select-input" [value]="$any(taskForm?.get('module_id')?.value)"
                      (change)="handleModuleChange($event)"
                      [disabled]="isViewMode || isReadOnly || !modules || modules.length === 0">
                      <option [value]="''" disabled [selected]="!$any(taskForm?.get('module_id')?.value)">
                        Select Module
                      </option>
                      @for (m of modules; track m) {
                        <option [value]="m.id">{{ m.name }}</option>
                      }
                    </select>
                    <mat-icon class="select-icon">expand_more</mat-icon>
                  </div>
                </div>
              }
              <!-- Row 3: Reference Number search (typeahead) -->
              @if (!($any(taskForm?.get('self_assign')?.value) == 1)) {
                <div class="form-field">
                  <label class="field-label">Select reference number</label>
                  <ng-select [items]="(referenceSearchResults$ | async) ?? []" bindLabel="name" [typeahead]="referenceSearchInput$"
                    [loading]="referenceSearchLoading" [searchable]="true" [clearable]="true"
                    [disabled]="!$any(taskForm?.get('module_id')?.value) || isViewMode || isReadOnly" placeholder="Search..."
                    (change)="onReferenceSelect($event)" class="user-search-select">
                    <ng-template ng-option-tmp let-item="item">
                      <div class="user-option">
                        <span class="user-name">{{ item.name }}</span>
                      </div>
                    </ng-template>
                    <ng-template ng-notfound-tmp>
                      <div class="no-results">
                        <mat-icon>search_off</mat-icon>
                        <span>No results</span>
                      </div>
                    </ng-template>
                  </ng-select>
                </div>
              }
            </div>
          </div>
        }
        <!-- Status & Priority Section -->
        <div class="sidebar-section">
          <h3 class="section-header">STATUS & PRIORITY</h3>
          <div class="section-content">
            <!-- Status Dropdown (Dynamic from API with colors and lock icons) -->
            <div class="form-field" [ngClass]="{'permission-disabled': isLocked}">
              <label class="field-label">Status</label>
              <mat-select class="status-select" [formControl]="$any(taskForm?.get('status'))"
                (selectionChange)="handleStatusChange($event)" placeholder="Select status..."
                [disabled]="isLocked" panelClass="status-select-panel">
                <!-- Custom Trigger: Shows selected status with color dot and lock icon -->
                <mat-select-trigger>
                  <div class="status-trigger-content">
                    <span class="status-color-dot" [style.background-color]="getSelectedStatusColor()">
                    </span>
                    <span class="status-trigger-name">
                      {{ getSelectedStatusName() || 'Select status...' }}
                      @if (getSelectedStatusLocked()) {
                        <mat-icon class="status-lock-icon" inline="true">
                          lock
                        </mat-icon>
                      }
                    </span>
                  </div>
                </mat-select-trigger>
                <!-- Options: Each status with color dot and lock icon -->
                @for (status of statuses; track status) {
                  <mat-option [value]="status.id" class="status-option">
                    <div class="status-item">
                      <!-- Colored dot from API -->
                      <span class="status-color-dot" [style.background-color]="status.color">
                      </span>
                      <!-- Status name with lock icon inline -->
                      <span class="status-item-name">
                        {{ status.name }}
                        <!-- Lock icon (informative only, option remains enabled) -->
                        @if (status.lock_status) {
                          <mat-icon class="status-lock-icon" inline="true">
                            lock
                          </mat-icon>
                        }
                      </span>
                    </div>
                  </mat-option>
                }
              </mat-select>
            </div>
            <!-- Priority Dropdown -->
            <div class="form-field" [ngClass]="{'permission-disabled': isLocked}">
              <label class="field-label">Priority</label>
              <div class="custom-select">
                <select class="select-input" [formControl]="$any(taskForm?.get('priority'))"
                  [disabled]="isViewMode || isReadOnly || isLocked" (change)="handleFieldChange('priority', $event)">
                  <option [value]="1">Low</option>
                  <option [value]="2">Medium</option>
                  <option [value]="3">High</option>
                  <option [value]="4">Urgent</option>
                </select>
                <mat-icon class="select-icon">expand_more</mat-icon>
              </div>
            </div>
          </div>
        </div>
        <!-- Dates & Milestones Section -->
        <div class="sidebar-section">
          <h3 class="section-header">DATES & MILESTONES</h3>
          <div class="section-content">
            <!-- Due Date -->
            <div class="form-field" [ngClass]="{'permission-disabled': isLocked}">
              <label class="field-label">Due Date</label>
              <input type="date" class="date-input" [value]="$any(taskForm?.get('due_date')?.value)"
                [disabled]="isViewMode || isReadOnly || isLocked" (change)="handleFieldChange('due_date', $event)">
            </div>
            <!-- Milestone Selector -->
            <div class="form-field">
              <label class="field-label">Milestone</label>
              <!-- Selected Milestone Display -->
              @if (selectedMilestone) {
                <div class="milestone-selected">
                  <div class="milestone-icon">
                    <mat-icon>flag</mat-icon>
                  </div>
                  <span class="milestone-name">{{ selectedMilestone.name }}</span>
                  @if (!isViewMode && !isReadOnly && !isLocked) {
                    <button type="button" class="milestone-remove-btn"
                      (click)="removeMilestone()" [attr.aria-label]="'Remove milestone'">
                      <mat-icon>close</mat-icon>
                    </button>
                  }
                </div>
              }
              <!-- Add Milestone Button -->
              @if (!selectedMilestone && !isViewMode && !isReadOnly && !isLocked) {
                <button type="button" class="milestone-add-btn"
                  (click)="toggleMilestoneSelector()" [attr.aria-label]="'Attach to milestone'">
                  <mat-icon>add</mat-icon>
                  <span>Attach to Milestone</span>
                </button>
              }
              <!-- Milestone Selector Popup -->
              @if (showMilestoneSelector) {
                <div class="milestone-popup" #milestonePopup>
                  <div class="popup-header">Select Milestone</div>
                  <div class="popup-content">
                    @for (milestone of milestones; track milestone) {
                      <div class="milestone-item" (click)="selectMilestone(milestone)">
                        <!-- Left: Status Indicator -->
                        <div class="milestone-status-dot" [class]="'status-' + milestone.status"></div>
                        <!-- Center: Main Content -->
                        <div class="milestone-details">
                          <!-- Row 1: Name + Status Badge -->
                          <div class="milestone-header">
                            <div class="milestone-item-name">{{ milestone.name }}</div>
                            <span class="milestone-status-badge" [class]="'badge-' + milestone.status">
                              {{ getMilestoneStatusText(milestone.status) }}
                            </span>
                          </div>
                          <!-- Row 2: Date Range or Due Date -->
                          <div class="milestone-date-info">
                            <mat-icon class="date-icon">calendar_today</mat-icon>
                            @if (milestone.startDate && milestone.endDate) {
                              <span>
                                {{ milestone.startDate | date: 'MMM d' }} - {{ milestone.endDate | date: 'MMM d, yyyy' }}
                              </span>
                            }
                            @if (!milestone.startDate || !milestone.endDate) {
                              <span>
                                Due: {{ milestone.dueDate | date: 'mediumDate' }}
                              </span>
                            }
                            <!-- Days Remaining (if in progress or upcoming) -->
                            @if (milestone.daysRemaining && milestone.status !== 'completed' && milestone.status !== 'overdue') {
                              <span class="days-remaining"
                                >
                                • {{ milestone.daysRemaining }} left
                              </span>
                            }
                          </div>
                          <!-- Row 3: Progress Bar (if in-progress) -->
                          @if (milestone.status === 'in-progress' && milestone.progress !== undefined) {
                            <div class="milestone-progress-row"
                              >
                              <div class="milestone-progress-bar">
                                <div class="progress-fill" [style.width.%]="milestone.progress"></div>
                              </div>
                              <span class="milestone-progress-text">{{ milestone.progress }}%</span>
                            </div>
                          }
                          <!-- Row 4: Task Stats (if available) -->
                          @if (milestone.taskStats) {
                            <div class="milestone-task-stats">
                              <mat-icon class="task-icon">check_circle_outline</mat-icon>
                              <span>{{ milestone.taskStats.completed }}/{{ milestone.taskStats.total }} tasks</span>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
            <!-- Created On (Read-only) -->
            <div class="form-field">
              <label class="field-label">Created On</label>
              <div class="field-value">{{ createdOn | date: 'mediumDate' }}</div>
            </div>
          </div>
        </div>
        <!-- TIME PLANNING SECTION (Collapsible) -->
        <div class="sidebar-section time-planning-section">
          <div class="section-header-collapsible" (click)="toggleTimePlanning()">
            <div class="section-title-wrapper">
              <mat-icon class="section-icon">schedule</mat-icon>
              <h3 class="section-header-inline">TIME PLANNING</h3>
            </div>
            <mat-icon class="expand-icon" [class.expanded]="isTimePlanningExpanded">
              {{ isTimePlanningExpanded ? 'expand_less' : 'expand_more' }}
            </mat-icon>
          </div>
          @if (isTimePlanningExpanded) {
            <div class="section-content" [@slideDown]>
              <!-- Start Date -->
              <div class="form-field" [ngClass]="{'permission-disabled': isLocked}">
                <label class="field-label">Start Task On</label>
                <input type="date" class="date-input" [value]="$any(taskForm?.get('start_date')?.value)"
                  [disabled]="isViewMode || isReadOnly || isLocked" (change)="onStartDateChange($event)">
              </div>
              <!-- Duration -->
              <div class="form-field" [ngClass]="{'permission-disabled': isLocked}">
                <label class="field-label">Duration</label>
                <div class="duration-input-wrapper">
                  <input type="number" [value]="taskForm?.get('duration')?.value || ''" (input)="onDurationChange($event)"
                    [disabled]="isViewMode || isReadOnly || isLocked" placeholder="Enter duration" min="0" class="duration-input">
                  <span class="duration-unit">days</span>
                </div>
              </div>
              <!-- Finish Date -->
              <div class="form-field" [ngClass]="{'permission-disabled': isLocked}">
                <label class="field-label">Finish</label>
                <input type="date" class="date-input" [value]="$any(taskForm?.get('date_finished')?.value)"
                  [disabled]="isViewMode || isReadOnly || isLocked" (change)="onFinishDateChange($event)">
              </div>
              <!-- Validation Message -->
              @if (timePlanningError) {
                <div class="validation-message error">
                  <mat-icon class="error-icon">error</mat-icon>
                  <span>{{ timePlanningError }}</span>
                </div>
              }
            </div>
          }
        </div>
        <!-- People Section -->
        <div class="sidebar-section">
          <h3 class="section-header">PEOPLE</h3>
          <div class="section-content">
            <!-- Assignee -->
            <div class="form-field" [ngClass]="{'permission-disabled': isLocked}">
              <div class="field-header">
                <label class="field-label">Assignee</label>
                @if (!isViewMode && !isReadOnly && !isLocked) {
                  <button type="button" class="field-add-btn"
                    (click)="openAssigneeSelector()" [attr.aria-label]="'Add assignee'">
                    <mat-icon>{{ showAssigneeSearch ? 'close' : 'add' }}</mat-icon>
                  </button>
                }
              </div>
              <!-- Inline Search (ng-select) -->
              @if (showAssigneeSearch) {
                <div class="inline-search-wrapper">
                  <ng-select #assigneeSelect [items]="assigneeItems" bindLabel="full_name"
                    placeholder="Search for assignee..." [searchable]="true" [clearable]="true"
                    [loading]="assigneeSearchLoading || assigneeLoadingMore" [typeahead]="assigneeSearchInput$"
                    [disabled]="isViewMode || isReadOnly" (change)="onAssigneeSelect($event)"
                    (scrollToEnd)="onAssigneeScrollToEnd()" class="user-search-select">
                    <ng-template ng-option-tmp let-item="item">
                      @if (item.__skeleton) {
                        <div class="user-option user-option-skeleton">
                          <div class="option-avatar-skeleton"></div>
                          <div class="user-info-skeleton">
                            <div class="skeleton-line skeleton-line-name"></div>
                            <div class="skeleton-line skeleton-line-email"></div>
                          </div>
                        </div>
                      } @else {
                        <div class="user-option">
                          <app-profile-avatar [fullName]="item.full_name" [profileUrl]="resolveProfileImageUrl(item.profile_image)"
                            widthSize="24px" heightSize="24px" class="option-avatar">
                          </app-profile-avatar>
                          <div class="user-info">
                            <span class="user-name">{{ item.full_name }}</span>
                            @if (item.email) {
                              <span class="user-email">{{ item.email }}</span>
                            }
                          </div>
                        </div>
                      }
                    </ng-template>
                    <ng-template ng-notfound-tmp>
                      <div class="no-results">
                        <mat-icon>search_off</mat-icon>
                        <span>No users found</span>
                      </div>
                    </ng-template>
                  </ng-select>
                </div>
              }
              <div class="people-list">
                <!-- Assignee List -->
                @for (assignee of assignees; track assignee; let i = $index) {
                  <div class="person-item">
                    <app-profile-avatar [fullName]="assignee.full_name || assignee.name"
                      [profileUrl]=" $any(assignee.profile_image)?.url  || ''" widthSize="32px" heightSize="32px"
                      class="person-avatar">
                    </app-profile-avatar>
                    <span class="person-name">{{ assignee.full_name || assignee.name }}</span>
                    @if (!isViewMode && !isReadOnly && !isLocked) {
                      <button type="button" class="person-default-star-btn"
                        [class.is-default]="isPersonDefault(assignee, 'assignee')"
                        [matTooltip]="isPersonDefault(assignee, 'assignee') ? 'Default' : 'Set as default'"
                        [attr.aria-label]="isPersonDefault(assignee, 'assignee') ? 'Default' : 'Set as default'"
                        (click)="setSelectedAssigneeAsDefault(assignee, $event)">
                        <mat-icon>{{ isPersonDefault(assignee, 'assignee') ? 'star' : 'star_border' }}</mat-icon>
                      </button>
                    } @else if (isPersonDefault(assignee, 'assignee')) {
                      <mat-icon class="default-user-star" matTooltip="Default">star</mat-icon>
                    }
                    @if (!isViewMode && !isReadOnly && !isLocked) {
                      <button type="button" class="person-remove-btn"
                        (click)="removeAssignee(i)" [attr.aria-label]="'Remove assignee'">
                        <mat-icon>close</mat-icon>
                      </button>
                    }
                  </div>
                }
                <!-- Empty State -->
                @if (assignees.length === 0 && !showAssigneeSearch) {
                  <div class="empty-state">
                    No assignees
                  </div>
                }
              </div>
            </div>
            <!-- Observers -->
            <div class="form-field" [ngClass]="{'permission-disabled': isLocked}">
              <div class="field-header">
                <label class="field-label">Observers</label>
                @if (!isViewMode && !isReadOnly && !isLocked) {
                  <button type="button" class="field-add-btn"
                    (click)="openObserverSelector()" [attr.aria-label]="'Add observer'">
                    <mat-icon>{{ showObserverSearch ? 'close' : 'add' }}</mat-icon>
                  </button>
                }
              </div>
              <!-- Inline Search (ng-select) -->
              @if (showObserverSearch) {
                <div class="inline-search-wrapper">
                  <ng-select #observerSelect [items]="observerItems" bindLabel="full_name"
                    placeholder="Search for observer..." [searchable]="true" [clearable]="true"
                    [loading]="observerSearchLoading || observerLoadingMore" [typeahead]="observerSearchInput$"
                    [disabled]="isViewMode || isReadOnly" (change)="onObserverSelect($event)"
                    (scrollToEnd)="onObserverScrollToEnd()" class="user-search-select">
                    <ng-template ng-option-tmp let-item="item">
                      @if (item.__skeleton) {
                        <div class="user-option user-option-skeleton">
                          <div class="option-avatar-skeleton"></div>
                          <div class="user-info-skeleton">
                            <div class="skeleton-line skeleton-line-name"></div>
                            <div class="skeleton-line skeleton-line-email"></div>
                          </div>
                        </div>
                      } @else {
                        <div class="user-option">
                          <app-profile-avatar [fullName]="item.full_name" [profileUrl]="resolveProfileImageUrl(item.profile_image)"
                            widthSize="24px" heightSize="24px" class="option-avatar">
                          </app-profile-avatar>
                          <div class="user-info">
                            <span class="user-name">{{ item.full_name }}</span>
                            @if (item.email) {
                              <span class="user-email">{{ item.email }}</span>
                            }
                          </div>
                        </div>
                      }
                    </ng-template>
                    <ng-template ng-notfound-tmp>
                      <div class="no-results">
                        <mat-icon>search_off</mat-icon>
                        <span>No users found</span>
                      </div>
                    </ng-template>
                  </ng-select>
                </div>
              }
              <div class="people-list">
                <!-- Observer List -->
                @for (observer of observers; track observer; let i = $index) {
                  <div class="person-item">
                    <app-profile-avatar [fullName]="observer.full_name || observer.name"
                      [profileUrl]=" $any(observer.profile_image)?.url || ''" widthSize="32px" heightSize="32px"
                      class="person-avatar">
                    </app-profile-avatar>
                    <span class="person-name">{{ observer.full_name || observer.name }}</span>
                    @if (!isViewMode && !isReadOnly && !isLocked) {
                      <button type="button" class="person-default-star-btn"
                        [class.is-default]="isPersonDefault(observer, 'observer')"
                        [matTooltip]="isPersonDefault(observer, 'observer') ? 'Default' : 'Set as default'"
                        [attr.aria-label]="isPersonDefault(observer, 'observer') ? 'Default' : 'Set as default'"
                        (click)="setSelectedObserverAsDefault(observer, $event)">
                        <mat-icon>{{ isPersonDefault(observer, 'observer') ? 'star' : 'star_border' }}</mat-icon>
                      </button>
                    } @else if (isPersonDefault(observer, 'observer')) {
                      <mat-icon class="default-user-star" matTooltip="Default">star</mat-icon>
                    }
                    @if (!isViewMode && !isReadOnly && !isLocked) {
                      <button type="button" class="person-remove-btn"
                        (click)="removeObserver(i)" [attr.aria-label]="'Remove observer'">
                        <mat-icon>close</mat-icon>
                      </button>
                    }
                  </div>
                }
                <!-- Empty State -->
                @if (observers.length === 0 && !showObserverSearch) {
                  <div class="empty-state">
                    No observers
                  </div>
                }
              </div>
            </div>
            <!-- Participants -->
            <div class="form-field" [ngClass]="{'permission-disabled': isLocked}">
              <div class="field-header">
                <label class="field-label">Participants</label>
                @if (!isViewMode && !isReadOnly && !isLocked) {
                  <button type="button" class="field-add-btn"
                    (click)="openParticipantSelector()" [attr.aria-label]="'Add participant'">
                    <mat-icon>{{ showParticipantSearch ? 'close' : 'add' }}</mat-icon>
                  </button>
                }
              </div>
              <!-- Inline Search (ng-select) -->
              @if (showParticipantSearch) {
                <div class="inline-search-wrapper">
                  <ng-select #participantSelect [items]="participantItems" bindLabel="full_name"
                    placeholder="Search for participant..." [searchable]="true" [clearable]="true"
                    [loading]="participantSearchLoading || participantLoadingMore" [typeahead]="participantSearchInput$"
                    [disabled]="isViewMode || isReadOnly" (change)="onParticipantSelect($event)"
                    (scrollToEnd)="onParticipantScrollToEnd()" class="user-search-select">
                    <ng-template ng-option-tmp let-item="item">
                      @if (item.__skeleton) {
                        <div class="user-option user-option-skeleton">
                          <div class="option-avatar-skeleton"></div>
                          <div class="user-info-skeleton">
                            <div class="skeleton-line skeleton-line-name"></div>
                            <div class="skeleton-line skeleton-line-email"></div>
                          </div>
                        </div>
                      } @else {
                        <div class="user-option">
                          <app-profile-avatar [fullName]="item.full_name" [profileUrl]="resolveProfileImageUrl(item.profile_image)"
                            widthSize="24px" heightSize="24px" class="option-avatar">
                          </app-profile-avatar>
                          <div class="user-info">
                            <span class="user-name">{{ item.full_name }}</span>
                            @if (item.email) {
                              <span class="user-email">{{ item.email }}</span>
                            }
                          </div>
                        </div>
                      }
                    </ng-template>
                    <ng-template ng-notfound-tmp>
                      <div class="no-results">
                        <mat-icon>search_off</mat-icon>
                        <span>No users found</span>
                      </div>
                    </ng-template>
                  </ng-select>
                </div>
              }
              <div class="people-list">
                <!-- Participant List -->
                @for (participant of participants; track participant; let i = $index) {
                  <div class="person-item">
                    <app-profile-avatar [fullName]="participant.full_name || participant.name"
                      [profileUrl]=" $any(participant.profile_image)?.url || ''" widthSize="32px" heightSize="32px"
                      class="person-avatar">
                    </app-profile-avatar>
                    <span class="person-name">{{ participant.full_name || participant.name }}</span>
                    @if (!isViewMode && !isReadOnly && !isLocked) {
                      <button type="button" class="person-default-star-btn"
                        [class.is-default]="isPersonDefault(participant, 'participant')"
                        [matTooltip]="isPersonDefault(participant, 'participant') ? 'Default' : 'Set as default'"
                        [attr.aria-label]="isPersonDefault(participant, 'participant') ? 'Default' : 'Set as default'"
                        (click)="setSelectedParticipantAsDefault(participant, $event)">
                        <mat-icon>{{ isPersonDefault(participant, 'participant') ? 'star' : 'star_border' }}</mat-icon>
                      </button>
                    } @else if (isPersonDefault(participant, 'participant')) {
                      <mat-icon class="default-user-star" matTooltip="Default">star</mat-icon>
                    }
                    @if (!isViewMode && !isReadOnly && !isLocked) {
                      <button type="button" class="person-remove-btn"
                        (click)="removeParticipant(i)" [attr.aria-label]="'Remove participant'">
                        <mat-icon>close</mat-icon>
                      </button>
                    }
                  </div>
                }
                <!-- Empty State -->
                @if (participants.length === 0 && !showParticipantSearch) {
                  <div class="empty-state">
                    No participants
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
        <!-- Tags Section - Unified for All Modes -->
        <div class="sidebar-section" [ngClass]="{'permission-disabled': isLocked}">
          <h3 class="section-header">TAGS</h3>
          <div class="section-content">
            <!-- Unified Tags Display -->
            <div class="tags-dropdown-wrapper">
              <!-- Normal Chips Display (When Dropdown Closed) -->
              @if (!isTagsDropdownOpen) {
                <div class="tags-container">
                  <!-- Empty State -->
                  @if (displayedTags.length === 0) {
                    <div class="tags-empty-state" [class.clickable]="!isViewMode && !isReadOnly && !isLocked"
                      (click)="!isViewMode && !isReadOnly && !isLocked && openTagsDropdown()">
                      <span>{{ isViewMode ? 'No tags added against this task' : 'No tags against this task' }}</span>
                    </div>
                  }
                  <!-- Chip Zone -->
                  @if (displayedTags.length > 0) {
                    <div class="tags-chip-zone" [class.clickable]="!isViewMode && !isReadOnly && !isLocked"
                      (click)="!isViewMode && !isReadOnly && !isLocked && openTagsDropdown()">
                      @for (tag of displayedTags; track tag; let i = $index) {
                        <div class="tag-chip"
                          [style.background-color]="getTagBackgroundColor(tag.color)"
                          [style.color]="getTagTextColor(tag.color)">
                          <span class="tag-name">{{ tag.name }}</span>
                          <!-- Remove button only in Create/Edit modes -->
                          @if (!isViewMode && !isReadOnly && !isLocked) {
                            <button type="button" class="tag-remove-btn"
                              (click)="removeTagFromDisplayed(i); $event.stopPropagation()" [attr.aria-label]="'Remove tag'">
                              <mat-icon>close</mat-icon>
                            </button>
                          }
                        </div>
                      }
                    </div>
                  }
                  <!-- Create and Add Tag Button - Only in Create/Edit modes -->
                  @if (!isViewMode && !isReadOnly && !isLocked) {
                    <button type="button" class="tag-add-btn" (click)="openTagSelector()"
                      [attr.aria-label]="'Create and add tag'">
                      <mat-icon>add</mat-icon>
                      <span>Create and Add Tag</span>
                    </button>
                  }
                </div>
              }
              <!-- ng-select Multi-Select (When Dropdown Open - CREATE/EDIT MODES ONLY) -->
              @if (isTagsDropdownOpen && !isViewMode) {
                <div class="tags-ng-select-wrapper">
                  <ng-select [items]="combinedTagsForDropdown" bindLabel="name" [compareWith]="compareTags"
                    [trackByFn]="trackByTagName" [multiple]="true" [closeOnSelect]="false" [searchable]="true"
                    [clearable]="true" [hideSelected]="false" placeholder="Search or select tags..."
                    [(ngModel)]="selectedTagsForDropdown" (change)="onTagsSelectChange($event)"
                    (open)="isTagsDropdownOpen = true" (close)="closeTagsDropdown()" class="tags-ng-select"
                    [class.ng-select-focused]="isTagsDropdownOpen" #tagsNgSelect>
                    <!-- Custom Tag Template (Chips inside input) -->
                    <ng-template ng-label-tmp let-item="item">
                      <div class="ng-select-tag-chip" [style.color]="getTagTextColor(item.color)"
                        [style.border-color]="getTagTextColor(item.color)">
                        <span>{{ item.name }}</span>
                      </div>
                    </ng-template>
                    <!-- Custom Option Template (Dropdown items with checkmarks) -->
                    <ng-template ng-option-tmp let-item="item" let-index="index">
                      <div class="tag-dropdown-item">
                        <span class="tag-color-dot" [style.background-color]="getTagTextColor(item.color)"></span>
                        <span class="tag-item-name">{{ item.name }}</span>
                        @if (isTagSelectedInDropdown(item)) {
                          <mat-icon class="tag-check-icon">check</mat-icon>
                        }
                      </div>
                    </ng-template>
                    <!-- Not Found Template -->
                    <ng-template ng-notfound-tmp>
                      <div class="tag-not-found">
                        <span>No tags found</span>
                      </div>
                    </ng-template>
                  </ng-select>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Sidebar Footer (Progress Indicator) -->
    @if (!isCollapsed) {
      <div class="task-sidebar__footer">
        <div class="progress-section">
          <div class="progress-header">
            <span class="progress-label">Task Progress</span>
            <span class="progress-percentage">{{ completionPercentage }}%</span>
          </div>
          <div class="progress-bar-container">
            <div class="progress-bar-fill" [style.width.%]="completionPercentage">
            </div>
          </div>
        </div>
      </div>
    }

  </div>

  <!-- Collapsed Sidebar Toggle Button -->
  @if (isCollapsed) {
    <button type="button" class="sidebar-toggle-collapsed"
      [@toggleButtonHover]="getToggleButtonAnimationState()" [@fadeInOut] (click)="expandSidebar()"
      (mouseenter)="isToggleButtonHovered = true" (mouseleave)="isToggleButtonHovered = false"
      [attr.aria-label]="'Expand sidebar'">
      <mat-icon>arrow_forward</mat-icon>
    </button>
  }
</div>